import { Component, type ReactNode } from 'react';
import { i18n } from '@/shared/i18n';

interface State {
  error: Error | null;
}

const RELOADED_KEY = 'els.chunk-error-reloaded';

const isChunkLoadError = (message: string): boolean =>
  message.includes('Failed to fetch dynamically imported module') ||
  message.includes('Importing a module script failed');

export class ChunkErrorBoundary extends Component<{ children: ReactNode }, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error): void {
    if (!isChunkLoadError(error.message)) {
      return;
    }
    try {
      if (sessionStorage.getItem(RELOADED_KEY)) {
        return;
      }
      sessionStorage.setItem(RELOADED_KEY, '1');
      window.location.reload();
    } catch {}
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) {
      return this.props.children;
    }
    if (isChunkLoadError(error.message)) {
      return (
        <div className="min-h-screen flex flex-col items-center justify-center gap-2 p-6 text-center">
          <p className="text-ink m-0">{i18n.getFixedT(null, 'app')('chunkError.newVersion')}</p>
          <button
            type="button"
            className="px-4 py-2 rounded-md bg-primary text-ink-inverse"
            onClick={() => {
              window.location.reload();
            }}
          >
            {i18n.getFixedT(null, 'app')('chunkError.reload')}
          </button>
        </div>
      );
    }
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-2 p-6 text-center">
        <p className="text-danger m-0">{i18n.getFixedT(null, 'app')('chunkError.generic')}</p>
        <pre className="text-xs text-ink-subtle m-0 max-w-xl whitespace-pre-wrap">
          {error.message}
        </pre>
      </div>
    );
  }
}
